import { Injectable } from '@angular/core';
import OpenSeadragon from 'openseadragon';
import { HttpClient } from '@angular/common/http';
import {
  ImageDimensions,
  Tile,
  TileTransformedExtents,
  TileSource,
} from '@app/osd-annotation/models/osd-kimera.model';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import { map, switchMap } from 'rxjs/operators';
import { OsdTransformOptions } from '@app/osd-annotation/osd-annotation.types';
import {
  ContentDTO,
  ImageSearchResponseModel,
} from '@app/osd-annotation/models/apiResponse.types';
import { IMAGE_SOURCE_TYPE } from '@shared/consts/platform-constants';

@Injectable({ providedIn: 'root' })
export class OsdKimeraService {
  private readonly imTransformedUrl = environment.kimeraImageFetchBaseUrl + '/v2/im_transformed_entitled';
  private readonly gridExtentsUrl =
    environment.kimeraImageFetchBaseUrl + '/v1/tensor_fetch/get_tile_transformed_extents';
  private _gridId!: string;
  private readonly metaMigrateContentLakeImageToKimeraUrl = `${environment.metaServiceBaseUrl}/contents/images/migrate`;
  private readonly metaImageSearchUrl = `${environment.metaServiceBaseUrl}/contents/images/search`;

  constructor(private readonly httpClient: HttpClient) {}

  set gridId(gridId: string) {
    this._gridId = gridId;
  }

  get gridId(): string {
    return this._gridId;
  }

  get tileSource$(): Observable<TileSource> {
    return this.gridExtents.pipe(
      map((tileData: TileTransformedExtents) => {
        const { pixels, origin } = this.calculateImageDimensions(tileData);
        const tileSize = 512;
        return {
          uc_offset: origin.x,
          ur_offset: origin.y,
          height: pixels.height,
          width: pixels.width,
          tileSize,
          tileOverlap: 0,
          maxLevel: this.getMaxLevel(pixels),
        } as TileSource;
      }),
    );
  }

  openTileSourceInsideTheViewer(tileSource: TileSource, viewer: OpenSeadragon.Viewer, transform?: OsdTransformOptions) {
    viewer.open({
      width: tileSource.width,
      height: tileSource.height,
      tileSize: tileSource.tileSize,
      tileOverlap: tileSource.tileOverlap,
      maxLevel: tileSource.maxLevel,
      getTileUrl: this.getTileUrlBasedOnTileSource(tileSource, transform),
    });
  }

  getTileUrlBasedOnTileSource(tileSource: TileSource, transform?: OsdTransformOptions) {
    return (level: any, x: any, y: any) => {
      const band = tileSource.maxLevel - level;
      const viewportSize = tileSource.tileSize * Math.pow(2, band);
      const ur = y * viewportSize + tileSource.ur_offset;
      const uc = x * viewportSize + tileSource.uc_offset;

      const transformQuery = this.getTransformQueryParams(transform);

      const remainingArgs = `grid_id=${this._gridId}&ur=${ur}&uc=${uc}&h=${viewportSize}&w=${viewportSize}&rw=${tileSource.tileSize}&rh=${tileSource.tileSize}&${transformQuery}ngsw-bypass=true`;
      return `${this.imTransformedUrl}?${remainingArgs}`;
    };
  }

  /**
   * Gets Kimera Grid ID from an image clObject ID.
   * Searches for the image using the objectId and subType, then handles based on source type:
   * - source_1: Calls migrate API to get kimeraGridId
   * - source_2: Returns kimeraGridId directly from the response
   * @param objectId The content lake object ID of the image
   * @param subType - image subtype
   * @returns Observable that emits the kimera grid ID
   */
  getKimeraGridIdFromObjectId(objectId: string, subType: string): Observable<string> {
    return this.httpClient.post<ImageSearchResponseModel>(this.metaImageSearchUrl, {
      subType: subType,
      subTypeId: objectId,
    }).pipe(
      switchMap((response: ImageSearchResponseModel) => {
        if (!response.images || response.images.length === 0) {
          throw new Error('No images found for the given objectId and subType');
        }

        const image = response.images[0];

        if (image.subType === IMAGE_SOURCE_TYPE.CONTENT_LAKE) {
          // For source_1, call migration API and return kimera_grid_id from the content
          return this.migrateContentLakeImageToKimeraGrid(image.id).pipe(
            map((content: ContentDTO) => content.content.kimera_grid_id)
          );
        } else if (image.subType === IMAGE_SOURCE_TYPE.KIMERA) {
          // For source_2, return kimera_grid_id directly from the content
          return [image.content.kimera_grid_id];
        } else {
          throw new Error(`Unsupported subType: ${image.subType}`);
        }
      })
    );
  }

  /**
   * Migrates a Content Lake image to Kimera Grid.
   * @param contentId - The ID of the Content Lake image to migrate
   * @returns Observable that emits the migration response
   */
  migrateContentLakeImageToKimeraGrid(contentId: string):Observable<ContentDTO>{
    return this.httpClient.post<ContentDTO>(this.metaMigrateContentLakeImageToKimeraUrl, {
      contentId
    })
  }

  private calculateImageDimensions(tileData: TileTransformedExtents): ImageDimensions {
    const nmPerPixel = tileData.nm_per_pixel;

    const tileKey = Object.keys(tileData).find((k) => /^r\d+_c\d+$/.test(k));
    if (!tileKey) {
      throw new Error('No valid tile found');
    }

    const firstTile = tileData[tileKey] as Tile;

    let minX = firstTile.x;
    let minY = firstTile.y;
    let maxX = firstTile.x + firstTile.w;
    let maxY = firstTile.y + firstTile.h;

    for (const [key, value] of Object.entries(tileData)) {
      if (!/^r\d+_c\d+$/.test(key)) continue;
      const tile = value as Tile;

      minX = Math.min(minX, tile.x);
      minY = Math.min(minY, tile.y);
      maxX = Math.max(maxX, tile.x + tile.w);
      maxY = Math.max(maxY, tile.y + tile.h);
    }

    const widthPx = maxX - minX;
    const heightPx = maxY - minY;

    return {
      pixels: { width: widthPx, height: heightPx },
      nmPerPixel,
      origin: { x: minX, y: minY },
      bbox: { minX, minY, maxX, maxY },
    };
  }

  private get gridExtents(): Observable<TileTransformedExtents> {
    const extentsUrl = `${this.gridExtentsUrl}?grid_id=${this._gridId}`;
    return this.httpClient.get<TileTransformedExtents>(extentsUrl);
  }

  private getMaxLevel({ height, width }: { height: number; width: number }): number {
    return Math.ceil(Math.log2(Math.max(width, height)));
  }

  get nmPerPixel$(): Observable<number> {
    return this.gridExtents.pipe(
      map((tileData: TileTransformedExtents) => {
        const { nmPerPixel } = this.calculateImageDimensions(tileData);
        if (nmPerPixel === -1) {
          throw new Error("Calibration data doesn't exist");
        }
        return nmPerPixel;
      }),
    );
  }

  private getTransformQueryParams(transform?: OsdTransformOptions): string {
    if (!transform) return '';
    return `brightness=${transform.brightness}&contrast=${transform.contrast}&gamma=${transform.gamma}&`;
  }
}
